import PropTypes from 'prop-types';
import { useRef, useEffect, useLayoutEffect, useMemo, useCallback, useState, forwardRef } from 'react';
import { useModal } from '../../../../../context/ModalContext';
import { useI18n } from '../../../../../context/I18nContext';
import TimelinePagination from '../../../../TimelinePagination';
import useScrollPagination from '../../../../../hooks/useScrollPagination';
import MessageInput from './MessageInput';
import UnreadMessageMarker from './UnreadMessageMarker';
import DateSeparator from './DateSeparator';
import SystemMessageSeparator from './SystemMessageSeparator';
import { useMessageDraftsContext } from '../../../../../context/MessageDraftsContext';
import { useReceiptType } from '../../../../../context/SettingsContext';
import { TIMELINE_SYSTEM_EVENT_TYPES, GEMATIK_ROOM, SYSTEM_MESSAGE_VISIBILITY, NON_EDITABLE_MESSAGE_TYPES } from '../../../../../constants';
import { useReadMarker } from '../../../../../hooks/useReadMarker';
import logger from '../../../../../utils/logger';
import { parseEmoteCommand } from '../../../../../utils/messageUtils';
import MessageGroup from './MessageGroup';
import draftStorageService from '../../../../../services/draftStorageService';
import './Messages.css';

const {
  SHOW_NAME_CHANGE,
  SHOW_TOPIC_CHANGE,
  SHOW_JOIN_RULES_CHANGE,
  SHOW_VISIBILITY_CHANGE,
  SHOW_POWER_LEVEL_CHANGE,
  SHOW_PINNED_EVENTS_CHANGE,
} = SYSTEM_MESSAGE_VISIBILITY;

/**
 * Check if a system event should be shown based on feature flags.
 * This mirrors the logic in SystemMessageSeparator.jsx to avoid rendering
 * date separators for hidden system messages.
 * Feature flags are defined in src/constants/messages.js (SYSTEM_MESSAGE_VISIBILITY).
 * @param {Object} msg - Matrix event object
 * @returns {boolean} - True if the system message should be shown
 */
const shouldShowSystemEvent = (msg) => {
  if (!msg?.getType) return false;

  const type = msg.getType();

  switch (type) {
    // Standard Matrix events - show based on visibility flags
    case 'm.room.name':
      return SHOW_NAME_CHANGE;

    case 'm.room.topic':
      return SHOW_TOPIC_CHANGE;

    // Gematik custom events - always hide (duplicates of standard events)
    // Both are sent for compatibility, but only standard events are displayed.
    // See: gemSpec_TI-M_Basis_V1.1.2 section 5.5.1.1
    case GEMATIK_ROOM.ROOM_NAME:
    case GEMATIK_ROOM.ROOM_TOPIC:
      return false;
    
    case 'm.room.join_rules':
      return SHOW_JOIN_RULES_CHANGE;
    
    case 'm.room.history_visibility':
      return SHOW_VISIBILITY_CHANGE;
    
    case 'm.room.power_levels':
      return SHOW_POWER_LEVEL_CHANGE;
    
    case 'm.room.pinned_events':
      return SHOW_PINNED_EVENTS_CHANGE;
    
    // These types are always shown
    case 'm.room.create':
    case 'm.room.avatar':
    case 'm.room.member':
    case 'm.room.tombstone':
    case 'm.room.encryption':
    case 'm.room.server_acl':
    case 'm.room.guest_access':
    case 'm.room.canonical_alias':
    case 'm.room.redaction':
      return true;
    
    default:
      // Unknown system event types are not shown
      return false;
  }
};

const Messages = forwardRef(({
  client,
  roomId,
  messages = [],
  typingText,
  disabled = false,
  disableSend = false, // Separate control for send button (defaults to disabled if not specified)
  allowEmptySend = false, // Allow sending empty messages (for NewChatView)
  placeholder = 'Type a message...',
  onSendMessage = () => {},
  onDeleteMessage = null, // Optional: only for existing chats
  onTriggerFileSelect = () => {},
  // Props for NewChatView (when context is not available)
  value: propValue,
  onSetMessage: propOnSetMessage,
  // Timeline pagination props
  isLoadingOlder = false,
  isLoadingNewer = false,
  canPaginateBackward = false,
  canPaginateForward = false,
  windowPosition = 'live',
  isFillingViewport = false,
  onPaginateBackward = () => {},
  onPaginateForward = () => {},
  onReactionDetailsOpen = () => {},
  onMessageDetailsOpen = () => {},
  onFocusMessage = () => {},
  onContactClick = () => {},
  selectedMessageId = null,
  readMarkerId = null,
  setReadMarker = () => {},
  onEditHistoryOpen = () => {},
  unreadCount = 0,
  jumpCompleted = 0,
  getLastVisibleEventId = null,
  // Message queue state (CLIENT A_25423)
  isQueueBusy = false,
  hasUnsentMessages = false,
  queueSnapshot = [],
  onRetryUnsent = () => {},
  onCancelUnsent = () => {},
}, ref) => {
  const { showError } = useModal();
  const { t } = useI18n();
  const receiptType = useReceiptType();

  // Default disableSend to disabled if not explicitly provided
  // This maintains backward compatibility for existing usage
  const effectiveDisableSend = disableSend !== undefined ? disableSend : disabled;
  
  // Consume the context to get all draft-related state and functions
  // Context may be null when used in NewChatView (no roomId)
  const draftsContext = useMessageDraftsContext();
  const {
    replyingTo = null,
    startReply = () => {},
    cancelReply = () => {},
    getReplyContent = () => ({}),
    editingMessage = null,
    startEdit = () => {},
    cancelEdit = () => {},
    getEditContent = () => ({}),
    restoreEditFromStorage = () => {},
    isRestoringRef = { current: false },
    currentMessage: contextCurrentMessage = '',
    onSetMessage: contextOnSetMessage = () => {},
    isSending = false,
    onSetSending = () => {},
  } = draftsContext || {};

  // Use prop values if context is not available (NewChatView scenario)
  // When context exists, use context values; otherwise fallback to props
  const currentMessage = draftsContext ? contextCurrentMessage : (propValue || '');
  // Memoize based on draftsContext presence to avoid re-creating function
  // propOnSetMessage is stable (state setter), so safe to use directly
  const onSetMessage = useMemo(
    () => draftsContext ? contextOnSetMessage : (propOnSetMessage || (() => {})),
    [draftsContext, contextOnSetMessage, propOnSetMessage],
  );
  
  const messagesListRef = useRef(null);
  const messageInputRef = useRef(null);
  const previousScrollHeightRef = useRef(0);
  const lastInitialScrollRef = useRef({ roomId: null, readMarkerId: null });
  const isMountedRef = useRef(true);
  const [scrollContainer, setScrollContainer] = useState(null);
  const showReadMarkerRef = useRef(false);
  const [, forceMarkerRender] = useState(0); // triggers re-render when ref changes in failure paths
  const markerInitializedForRoomRef = useRef(null); // Track which room we've initialized the marker for
  const capturedReadMarkerIdRef = useRef(null); // Capture the readMarkerId when marker is first shown

  // Track mount/unmount state to prevent state updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Callback ref to handle both forwarded ref and internal ref
  const setMessagesListRef = useCallback((node) => {
    messagesListRef.current = node;

    // Forward the ref to parent if provided
    if (ref) {
      if (typeof ref === 'function') {
        ref(node);
      } else {
        ref.current = node;
      }
    }
  }, [ref]);

  const { updateReadMarkerOnSend } = useReadMarker({
    scrollContainer,
    roomId,
    readMarkerId,
    setReadMarker,
    client,
    receiptType,
    getLastVisibleEventId,
  });

  useEffect(() => {
    if (messagesListRef.current && scrollContainer !== messagesListRef.current) {
      setScrollContainer(messagesListRef.current);
    }
  }, [scrollContainer, roomId]);

  // Set up scroll pagination
  const { scrollToBottom, maintainScrollPosition, autoScrollToNewMessages } = useScrollPagination({
    scrollContainer,
    canPaginateBackward,
    canPaginateForward,
    isLoadingOlder,
    isLoadingNewer,
    isFillingViewport,
    onPaginateBackward,
    onPaginateForward,
  });

  useEffect(() => {
    if (isLoadingOlder || isFillingViewport || windowPosition !== 'live') {
      return;
    }

    if (messagesListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;

      // Only auto-scroll if user is already near the bottom.
      if (scrollBottom <= 200) {
        messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
      }
    }
  }, [messages, windowPosition, isLoadingOlder, isFillingViewport]);

  // Focus input when reply is started
  useEffect(() => {
    if (replyingTo && messageInputRef.current) {
      messageInputRef.current.focus();
    }
  }, [replyingTo]);

  // Handle scroll position when new messages are loaded at the top
  useEffect(() => {
    // Only maintain scroll position when we have a previous height reference
    // and we're not at the very beginning
    if (messagesListRef.current && previousScrollHeightRef.current > 0) {
      maintainScrollPosition(previousScrollHeightRef.current);
    }
    
    // Always update the previous scroll height for next time
    if (messagesListRef.current) {
      previousScrollHeightRef.current = messagesListRef.current.scrollHeight;
    }
  }, [messages, maintainScrollPosition, isLoadingOlder, windowPosition]);

  // Auto-scroll when new live messages arrive (only if user is near bottom)
  useEffect(() => {
    // Only auto-scroll for new live messages, not when loading older messages
    if (windowPosition === 'live' && messages.length > 0 && !isLoadingOlder && !isFillingViewport) {
      autoScrollToNewMessages();
    }
  }, [messages.length, windowPosition, autoScrollToNewMessages, isLoadingOlder, isFillingViewport]);

  // Scroll to the correct position when the room changes.
  useLayoutEffect(() => {
    if (messages.length === 0 || windowPosition !== 'live' || !messagesListRef.current) {
      return;
    }

    const prev = lastInitialScrollRef.current;

    // Only scroll on initial room load (roomId change) or when readMarkerId changes
    // (unread marker data arrived after messages loaded). Skip for messages.length-only
    // changes (e.g., pin events, new messages) to prevent unwanted scroll-to-bottom.
    // NED-3232: Pin events add m.room.pinned_events to timeline, changing messages.length.
    // Note: messages.length must stay in the dep array so this effect fires when messages
    // first load (0→N), but the ref guard prevents it from scrolling on subsequent changes.
    if (prev.roomId === roomId && prev.readMarkerId === readMarkerId) {
      return;
    }

    // By using useLayoutEffect, we can scroll before the browser paints, preventing a flash.
    const unreadMarker = messagesListRef.current.querySelector('#unread-marker');
    logger.debug('[NED-3388] Scroll useLayoutEffect', { foundUnreadMarker: !!unreadMarker, roomId, readMarkerId });
    if (unreadMarker) {
      unreadMarker.scrollIntoView({ behavior: 'auto', block: 'center' });
      logger.debug('Scrolled to unread marker on room load.');
    } else {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }

    lastInitialScrollRef.current = { roomId, readMarkerId };
  }, [roomId, messages.length, readMarkerId]);

  // Scroll to unread marker after jumpToEvent loads the marker into the timeline.
  // Separate from the main scroll effect to avoid weakening the NED-3232 guard.
  useLayoutEffect(() => {
    if (!jumpCompleted || !messagesListRef.current) return;
    const unreadMarker = messagesListRef.current.querySelector('#unread-marker');
    if (unreadMarker) {
      unreadMarker.scrollIntoView({ behavior: 'auto', block: 'center' });
      logger.debug('[Messages] Scrolled to unread marker after jump');
    }
  }, [jumpCompleted]);

  // Focus input and set/clear text when edit state changes.
  useEffect(() => {
    if (editingMessage && messageInputRef.current) {
      messageInputRef.current.focus();
      if (!isRestoringRef.current) {
        onSetMessage(editingMessage.originalContent);
      }
    } else if (!editingMessage && !isRestoringRef.current && draftsContext) {
      // Only clear when draftsContext exists (skip for NewChatView)
      onSetMessage('');
    }
  // draftsContext is intentionally omitted - it's a stable reference that doesn't change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingMessage, isRestoringRef, onSetMessage]);

  // Synchronous marker initialization during render (not in an effect).
  // By computing showReadMarker synchronously, the marker is in the DOM
  // before useLayoutEffect runs, preventing the race condition where
  // useLayoutEffect queries #unread-marker before useEffect sets showReadMarker.
  if (markerInitializedForRoomRef.current !== roomId) {
    showReadMarkerRef.current = false;
    markerInitializedForRoomRef.current = null;
    capturedReadMarkerIdRef.current = null;
  }
  if (markerInitializedForRoomRef.current === null && messages.length > 0 && readMarkerId) {
    if (unreadCount > 0) {
      logger.debug('[ReadMarker] Showing marker for room with unreads:', {
        roomId,
        unreadCount,
        messagesCount: messages.length,
        readMarkerId,
      });
      showReadMarkerRef.current = true;
      capturedReadMarkerIdRef.current = readMarkerId;
    }
    markerInitializedForRoomRef.current = roomId;
  }
  logger.debug('[NED-3388] Marker render-time computation', { showReadMarker: showReadMarkerRef.current, unreadCount, readMarkerId, messagesLen: messages.length, markerInitFor: markerInitializedForRoomRef.current });
  const showReadMarker = showReadMarkerRef.current;

  const handleDeleteMessage = async (messageId, reason) => {
    try {
      if (onDeleteMessage) {
        await onDeleteMessage(messageId, reason);
      }
    } catch (error) {
      // Only show error if component is still mounted
      if (isMountedRef.current) {
        showError(
          t('Messages.deleteErrorTitle'),
          error?.translationKey
            ? t(error.translationKey, {}, error.message)
            : t('Messages.deleteErrorMessage', { error: error.message }),
        );
      }
    }
  };

  const handleReplyMessage = (message, senderName, content, msgtype, filename, thumbnailUrl, size) => {
    if (editingMessage) {
      cancelEdit();
      onSetMessage('');
    }
    startReply(message, senderName, content, msgtype, filename, thumbnailUrl, size);
  };

  const handleEditMessage = (message, content) => {
    if (replyingTo) {
      cancelReply();
    }
    startEdit(message, content);
  };

  const isAtRoomStart = useMemo(() => {
    if (!messages || messages.length === 0) {
      return false;
    }
    
    // Check if the first message group contains a room creation event
    const firstGroup = messages[0];
    const firstMessage = firstGroup?.messages?.[0];
    return firstMessage?.getType && firstMessage.getType() === 'm.room.create';
  }, [messages]);

  const retryingTransactionIds = useMemo(() => {
    return new Set(
      queueSnapshot
        .filter(({ txnId, retryCount, status }) => (
          txnId &&
          retryCount > 0 &&
          (status === 'queued' || status === 'sending' || status === 'retrying')
        ))
        .map(({ txnId }) => txnId),
    );
  }, [queueSnapshot]);

  // Transaction IDs for items currently queued/sending (including initial attempts)
  const pendingTransactionIds = useMemo(() => {
    return new Set(
      queueSnapshot
        .filter(({ txnId, status }) => (
          txnId && (status === 'queued' || status === 'sending')
        ))
        .map(({ txnId }) => txnId),
    );
  }, [queueSnapshot]);

  // Transaction IDs for items marked as unsent (user-visible failure)
  const unsentTransactionIds = useMemo(() => {
    return new Set(
      queueSnapshot
        .filter(({ txnId, status }) => (
          txnId && status === 'unsent'
        ))
        .map(({ txnId }) => txnId),
    );
  }, [queueSnapshot]);

  const renderMessageGroups = () => {
    const elements = [];
    let prevMessageTimeSeconds = null;
    
    // Track if marker is in a system event group - if so, render it before the next regular message group
    // This handles the case where the read marker points to a system event (like m.room.member join event)
    let pendingMarkerFromSystemEvent = false;
    const markerIdToUse = capturedReadMarkerIdRef.current || readMarkerId;

    messages.forEach((group) => {
      const firstMessage = group.messages?.[0];
      const timestampMs = firstMessage?.getTs ? firstMessage.getTs() : null;
      const timestampSeconds = typeof timestampMs === 'number' ? Math.floor(timestampMs / 1000) : null;

      const systemEventMsg = group.messages.find(
        msg => msg?.getType && TIMELINE_SYSTEM_EVENT_TYPES.includes(msg.getType()),
      );

      // Check if system message will actually be shown (some are hidden by feature flags)
      const shouldShowSystemMessage = systemEventMsg ? shouldShowSystemEvent(systemEventMsg) : false;

      let needsSeparator = false;
      if (timestampSeconds) {
        const oneDayInMs = 1000 * 60 * 60 * 24;
        const curDate = new Date(timestampSeconds * 1000);
        const prevDate = prevMessageTimeSeconds ? new Date(prevMessageTimeSeconds * 1000) : null;

        if (prevDate && (
          curDate.getTime() - prevDate.getTime() >= oneDayInMs ||
          prevDate.getDate() !== curDate.getDate() ||
          prevDate.getMonth() !== curDate.getMonth() ||
          prevDate.getFullYear() !== curDate.getFullYear()
        )) {
          needsSeparator = true;
        }
        // Only add date separator if there's a visible message
        // For system messages, only show separator if the system message will be shown
        // For regular message groups, always show the separator
        if (needsSeparator && (shouldShowSystemMessage || !systemEventMsg)) {
          elements.push(
            <DateSeparator key={`separator-${group.key}`} unixTime={timestampSeconds} />,
          );
        }
        prevMessageTimeSeconds = timestampSeconds;
      }

      // If this group contains a system event, decide whether to render it and then skip regular messages
      if (systemEventMsg) {
        // Check if the read marker is in this system event group
        // If so, we need to render the marker before the next regular message group
        if (showReadMarker && markerIdToUse) {
          const markerInSystemGroup = group.messages.some(m => m.getId?.() === markerIdToUse);
          if (markerInSystemGroup) {
            pendingMarkerFromSystemEvent = true;
          }
        }
        if (shouldShowSystemMessage) {
          elements.push(
            <SystemMessageSeparator
              key={`system-separator-${group.key}`}
              unixTime={timestampSeconds}
              msg={systemEventMsg}
            />,
          );
        }
        return;
      }

      // --- Read Marker Handling ---
      // Check if we have a pending marker from a system event group that should be rendered now
      if (pendingMarkerFromSystemEvent && showReadMarker) {
        // Render the marker before this regular message group
        elements.push(<UnreadMessageMarker key={`unread-marker-before-${group.key}`} />);
        pendingMarkerFromSystemEvent = false; // Clear the pending flag
      }

      // --- Read Marker Splitting Logic ---
      // Use captured readMarkerId (from when we first entered the room) for stable positioning
      // This prevents the marker from moving if readMarkerId changes due to read receipts
      const readMarkerIndex = showReadMarker && markerIdToUse
        ? group.messages.findIndex(msg => msg.getId() === markerIdToUse)
        : -1;

      if (readMarkerIndex > -1 && readMarkerIndex < group.messages.length - 1) {
        const readPart = group.messages.slice(0, readMarkerIndex + 1);
        const unreadPart = group.messages.slice(readMarkerIndex + 1);

        // Extract key from group to avoid React key prop spreading warning
        const { key: groupKey, ...groupPropsWithoutKey } = group;
        
        elements.push(
          <MessageGroup
            key={`${groupKey}-read`}
            {...groupPropsWithoutKey} // Pass all group props except 'key'
            messages={readPart}
            allMessageGroups={messages}
            onDeleteMessage={handleDeleteMessage}
            onReplyMessage={handleReplyMessage}
            onEditMessage={handleEditMessage}
            editHighlightedMessageId={editingMessage?.id}
            selectedMessageId={selectedMessageId}
            onReactionDetailsOpen={onReactionDetailsOpen}
            onMessageDetailsOpen={onMessageDetailsOpen}
            onFocusMessage={onFocusMessage}
            onContactClick={onContactClick}
            onEditHistoryOpen={onEditHistoryOpen}
            retryingTransactionIds={retryingTransactionIds}
            pendingTransactionIds={pendingTransactionIds}
            unsentTransactionIds={unsentTransactionIds}
          />,
        );

        if (showReadMarker) {
          elements.push(<UnreadMessageMarker key={`unread-marker-${groupKey}`} />);
        }

        elements.push(
          <MessageGroup
            key={`${groupKey}-unread`}
            {...groupPropsWithoutKey} // Pass all group props except 'key'
            messages={unreadPart}
            allMessageGroups={messages}
            onDeleteMessage={handleDeleteMessage}
            onReplyMessage={handleReplyMessage}
            onEditMessage={handleEditMessage}
            editHighlightedMessageId={editingMessage?.id}
            selectedMessageId={selectedMessageId}
            onReactionDetailsOpen={onReactionDetailsOpen}
            onMessageDetailsOpen={onMessageDetailsOpen}
            onFocusMessage={onFocusMessage}
            onContactClick={onContactClick}
            onEditHistoryOpen={onEditHistoryOpen}
            retryingTransactionIds={retryingTransactionIds}
            pendingTransactionIds={pendingTransactionIds}
            unsentTransactionIds={unsentTransactionIds}
          />,
        );
      } else {
        // Extract key from group to avoid React key prop spreading warning
        const { key: groupKey, ...groupPropsWithoutKey } = group;
        
        elements.push(
          <MessageGroup
            key={groupKey}
            {...groupPropsWithoutKey} // Pass all group props except 'key'
            allMessageGroups={messages}
            onDeleteMessage={handleDeleteMessage}
            onReplyMessage={handleReplyMessage}
            onEditMessage={handleEditMessage}
            editHighlightedMessageId={editingMessage?.id}
            selectedMessageId={selectedMessageId}
            onReactionDetailsOpen={onReactionDetailsOpen}
            onMessageDetailsOpen={onMessageDetailsOpen}
            onFocusMessage={onFocusMessage}
            onContactClick={onContactClick}
            onEditHistoryOpen={onEditHistoryOpen}
            retryingTransactionIds={retryingTransactionIds}
            pendingTransactionIds={pendingTransactionIds}
            unsentTransactionIds={unsentTransactionIds}
          />,
        );

        if (showReadMarker && readMarkerIndex === group.messages.length - 1) {
          const { key: groupKey } = group;
          elements.push(<UnreadMessageMarker key={`unread-marker-${groupKey}`} />);
        }
      }
    });

    return elements;
  };

  const handleSendMessage = useCallback(async () => {
    // Capture the current message value immediately to prevent race conditions
    const currentMessageText = currentMessage;
    // Capture the marker state so we can restore it if send fails
    const wasMarkerShown = showReadMarkerRef.current;
    let content;
    let result;

    // Clear message immediately (optimistic clearing)
    onSetMessage('');

    // Set sending state to prevent concurrent sends
    if (!isMountedRef.current) return null;
    onSetSending(true);

    // When the user sends a message, they have caught up with the conversation.
    // Hide the "New Messages" marker IMMEDIATELY (optimistically), before the async send completes.
    // This ensures the UI responds instantly rather than waiting for the API round-trip.
    // Note: We hide the marker for both new messages and edits - in both cases the user is "caught up".
    if (wasMarkerShown) {
      logger.debug('[ReadMarker] Hiding marker optimistically - user is sending a message');
      showReadMarkerRef.current = false;
      capturedReadMarkerIdRef.current = null; // Clear captured marker position
    }

    // Snapshot edit state before the try block so it can be restored on failure.
    let editSnapshot = null;

    // Restore edit state from a snapshot, handling both normal edits (with originalEvent)
    // and storage-restored edits (originalEvent is null, uses stored event ID instead).
    // messageText overrides originalContent so the useEffect that syncs editingMessage
    // to the input field restores the text the user was trying to send, not the pre-edit text.
    const restoreEditState = (snapshot, messageText) => {
      const content = messageText || snapshot.originalContent;
      if (snapshot.originalEvent) {
        startEdit(snapshot.originalEvent, content);
      } else if (snapshot.id) {
        restoreEditFromStorage({ id: snapshot.id, originalContent: content });
      }
    };

    try {
      if (editingMessage) {
        // Validate that the message type is editable before attempting to edit
        const msgtype = editingMessage.originalEvent?.getContent()?.msgtype;

        if (msgtype && NON_EDITABLE_MESSAGE_TYPES.includes(msgtype)) {
          logger.warn('[Messages] Attempted to edit non-text message type:', msgtype);
          cancelEdit();
          return null;
        }

        content = getEditContent(currentMessageText);
        editSnapshot = editingMessage;
        // Cancel edit state immediately after capturing content, before the async send.
        // This prevents a race condition (NED-3321) where the server response from this edit
        // arrives after the user starts a new edit, causing cancelEdit() to clear the new
        // edit's state and send the next edit as a plain message instead.
        cancelEdit();
        result = await onSendMessage(content);
        // ChatView.handleSendMessage swallows errors and returns false, so the catch
        // block below is unreachable for send failures via that path. Restore edit state
        // here when the send signals failure via a falsy return value.
        if (!result && editSnapshot && isMountedRef.current) {
          restoreEditState(editSnapshot, currentMessageText);
          if (wasMarkerShown) {
            showReadMarkerRef.current = true;
            forceMarkerRender(c => c + 1);
          }
          return null;
        }
      } else if (replyingTo) {
        content = getReplyContent(currentMessageText);
        result = await onSendMessage(content);
        if (result && isMountedRef.current) {
          cancelReply();
        }
      } else {
        // Parse /me emote command if present, otherwise send as text
        content = parseEmoteCommand(currentMessageText);
        result = await onSendMessage(content);
      }

      if (result && result.event_id && isMountedRef.current) {
        logger.debug('[ReadMarker] Setting read marker because user sent a message:', result.event_id);
        updateReadMarkerOnSend(result.event_id, roomId);
        // Always scroll to bottom so user sees their sent message
        scrollToBottom();
      }

      return result;
    } catch (error) {
      logger.error('Failed to send message:', error);

      // Restore read marker if it was hidden optimistically
      // (user hasn't actually caught up if the send failed)
      if (wasMarkerShown && isMountedRef.current) {
        logger.debug('[ReadMarker] Restoring marker - message send failed');
        showReadMarkerRef.current = true;
        forceMarkerRender(c => c + 1);
      }

      // Restore edit state so user can retry the edit (NED-3321: cancelEdit runs before
      // the async send, so we must restore it on failure to keep the user in edit mode).
      // Pass currentMessageText so the useEffect that syncs editingMessage to the input
      // restores the attempted edit text, not the pre-edit original.
      if (editSnapshot && isMountedRef.current) {
        restoreEditState(editSnapshot, currentMessageText);
      }

      // Restore message so user can retry
      if (isMountedRef.current) {
        // Component still mounted - restore to UI (also serves as immediate update
        // before the useEffect from restoreEditState fires on the next render)
        onSetMessage(currentMessageText);
      } else {
        // Component unmounted (user switched rooms) - save to draft storage
        // Note: Cannot save draft for NewChatView (roomId is null until room created)
        if (roomId) {
          draftStorageService.saveDraft(roomId, { text: currentMessageText });
        }
      }

      // Only show error modal if component is still mounted
      if (!isMountedRef.current) {
        return null;
      }

      // Differentiate error types for better user feedback
      let errorMessage;
      const errorTitle = t('Messages.sendErrorTitle');

      // Check for network errors (connection issues, timeouts)
      if (error.name === 'NetworkError' || error.errcode === 'M_NETWORK_ERROR' || !navigator.onLine) {
        errorMessage = t('Messages.sendErrorNetwork');
      }
      // Check for rate limiting (429 status)
      else if (error.httpStatus === 429 || error.errcode === 'M_LIMIT_EXCEEDED') {
        errorMessage = t('Messages.sendErrorRateLimit');
      }
      // Check for message too large (413 status)
      else if (error.httpStatus === 413 || error.errcode === 'M_TOO_LARGE') {
        errorMessage = t('Messages.sendErrorMessageTooLarge');
      }
      // Check for permission errors (403 status)
      else if (error.httpStatus === 403 || error.errcode === 'M_FORBIDDEN') {
        errorMessage = t('Messages.sendErrorPermission');
      }
      // Generic error with the actual error message
      else {
        errorMessage = t('Messages.sendErrorGeneric', { errorMessage: error.message || 'Unknown error' });
      }

      showError(errorTitle, errorMessage);
      return null;
    } finally {
      // Always clear sending state, even if send failed (only if still mounted)
      if (isMountedRef.current) {
        onSetSending(false);
      }
    }
  }, [
    editingMessage, currentMessage, replyingTo, onSendMessage, getEditContent, getReplyContent,
    cancelEdit, startEdit, restoreEditFromStorage, cancelReply, updateReadMarkerOnSend, onSetMessage, onSetSending, showError, t,
    roomId, scrollToBottom,
  ]);

    return (
        <div className="messages">
            <TimelinePagination
                isLoadingOlder={(isLoadingOlder || isFillingViewport) && !isAtRoomStart}
                isLoadingNewer={isLoadingNewer}
            />
            <div className="messages-list" ref={setMessagesListRef}>
                {renderMessageGroups()}
            </div>
            <div className="messages-bottom-spacer" />
            {
                (typingText) && (
                    <span className="messages-list-typing-indicator">
                        {typingText}
                    </span>
                )
            }
            <MessageInput
                ref={messageInputRef}
                roomId={roomId}
                disabled={disabled}
                disableSend={effectiveDisableSend}
                allowEmptySend={allowEmptySend}
                message={currentMessage}
                placeholder={placeholder}
                replyingTo={replyingTo}
                editingMessage={editingMessage}
                onCancelReply={cancelReply}
                onCancelEdit={() => {
                    cancelEdit();
                    onSetMessage('');
                }}
                onSetMessage={onSetMessage}
                onSendMessage={handleSendMessage}
                onSendLocationMessage={onSendMessage}
                onTriggerFileSelect={onTriggerFileSelect}
                isSending={isSending}
                isQueueBusy={isQueueBusy}
                hasUnsentMessages={hasUnsentMessages}
                onRetryUnsent={onRetryUnsent}
                onCancelUnsent={onCancelUnsent}
            />
        </div>
    );
});

Messages.displayName = 'Messages';

Messages.propTypes = {
  client: PropTypes.object,
  roomId: PropTypes.string, // Optional - can be null in NewChatView
  typingText: PropTypes.string,
  messages: PropTypes.array,
  disabled: PropTypes.bool, // Externally controlled disable (e.g., edit mode, room permissions). Note: isSending is provided by MessageDraftsContext, not props
  disableSend: PropTypes.bool,
  allowEmptySend: PropTypes.bool,
  placeholder: PropTypes.string,
  value: PropTypes.string, // For NewChatView when context is not available
  onSetMessage: PropTypes.func, // For NewChatView when context is not available
  onSendMessage: PropTypes.func,
  onDeleteMessage: PropTypes.func,
  onTriggerFileSelect: PropTypes.func,
  isLoadingOlder: PropTypes.bool,
  isLoadingNewer: PropTypes.bool,
  canPaginateBackward: PropTypes.bool,
  canPaginateForward: PropTypes.bool,
  windowPosition: PropTypes.oneOf(['live', 'historical', 'middle']),
  isFillingViewport: PropTypes.bool,
  onPaginateBackward: PropTypes.func,
  onPaginateForward: PropTypes.func,
  onReactionDetailsOpen: PropTypes.func,
  onMessageDetailsOpen: PropTypes.func,
  onFocusMessage: PropTypes.func,
  onContactClick: PropTypes.func,
  selectedMessageId: PropTypes.string,
  readMarkerId: PropTypes.string,
  setReadMarker: PropTypes.func,
  onEditHistoryOpen: PropTypes.func,
  unreadCount: PropTypes.number,
  jumpCompleted: PropTypes.number,
  getLastVisibleEventId: PropTypes.func,
  // Message queue state (CLIENT A_25423)
  isQueueBusy: PropTypes.bool,
  hasUnsentMessages: PropTypes.bool,
  queueSnapshot: PropTypes.array,
  onRetryUnsent: PropTypes.func,
  onCancelUnsent: PropTypes.func,
};

export default Messages;
